import { useCallback, useEffect, useState } from 'react';

import { faCircleCheck, faCirclePlay } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OrgMemberInfo, useAuthInfo } from '@propelauth/react';

import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useCompleteGuide } from '@/api/mutations/useUpdateCompletedGuides';
import FileIcon from '@/assets/guide/homepage-guides/file-icon.svg?react';
import HammerAndWrenchIcon from '@/assets/guide/homepage-guides/hammer-and-wrench-icon.svg?react';
import MagnifyingGlassIcon from '@/assets/guide/homepage-guides/magnifying-glass-icon.svg?react';
import QuestionIcon from '@/assets/guide/homepage-guides/question-icon.svg?react';
import CustomModal from '@/components/molecules/Modals/CustomModal';
import { GuideState, OrgUserWithMetadata } from '@/types';

interface Guide {
  title: string;
  description: string;
  icon: React.ReactNode;
  value: string;
  status: GuideState;
}

// Guides before the complete/not complete status gets added by the backend data
const GUIDES_WITHOUT_STATUS = [
  {
    title: 'Viewing documents',
    description: 'Learn how to view documents in Wexler',
    icon: <FileIcon className="h-4 w-4" />,
    value: 'viewing-documents',
  },
  {
    title: 'Exploring facts',
    description: 'Learn how to view the facts Wexler has extracted',
    icon: <MagnifyingGlassIcon className="h-4 w-4" />,
    value: 'exploring-facts',
  },
  {
    title: 'Using Kim',
    description: 'Learn how to use our AI agent, Kim, to ask questions',
    icon: <QuestionIcon className="h-4 w-4" />,
    value: 'using-kim',
  },
  {
    title: 'Creating a case',
    description: 'Learn how to create your first case',
    icon: <HammerAndWrenchIcon className="h-4 w-4" />,
    value: 'creating-a-case',
  },
  {
    title: 'Creating a matter',
    description: 'Learn how to create your first matter',
    icon: <HammerAndWrenchIcon className="h-4 w-4" />,
    value: 'creating-a-matter',
  },
];

const MODAL_CONTENT: Record<string, { title: string; description: string; link: string }> = {
  'creating-a-matter': {
    title: 'Creating a matter',
    description: 'Watch the video below to learn how to create your first matter',
    link: 'https://www.loom.com/embed/87a9c58bf4d44719a6a7993eb6f44650',
  },
  'creating-a-case': {
    title: 'Creating a case',
    description: 'Watch the video below to learn how to create your first case',
    link: 'https://www.loom.com/embed/8ca41397b9594951a011b8527a4b8e95',
  },
  'using-kim': {
    title: 'Using Kim',
    description: 'Watch the video below to learn how to use our AI agent, Kim, to ask questions',
    link: 'https://www.loom.com/embed/a605d42c38d943099010bd7974d778bf',
  },
  'exploring-facts': {
    title: 'Exploring facts',
    description: 'Watch the video below to learn how to view the facts Wexler has extracted',
    link: 'https://www.loom.com/embed/d6fdc9d8f5ec4c12856225b01989c682',
  },
  'viewing-documents': {
    title: 'Viewing documents',
    description: 'Watch the video below to learn how to view documents in Wexler',
    link: 'https://www.loom.com/embed/81b2853f7790487ebc42657ef7b20b7e',
  },
};

const GuideModal = ({
  title,
  description,
  guideName,
  link,
  state,
  onCompleteGuide,
}: {
  title: string;
  description: string;
  guideName: string;
  link: string;
  state: GuideState;
  onCompleteGuide: () => void;
}) => {
  const { mutate: updateCompletedGuide } = useCompleteGuide();

  const markAsComplete = useCallback(() => updateCompletedGuide({ guideName }), [guideName, updateCompletedGuide]);

  useEffect(() => {
    if (state === GuideState.NOT_STARTED) {
      markAsComplete();
      onCompleteGuide();
    }
  }, [state, markAsComplete, onCompleteGuide]);

  return (
    <div className="p-6">
      <h2 className="mb-4 text-lg font-semibold text-gray-900">{title}</h2>
      <p className="text-sm text-gray-600">{description}</p>

      <div className="my-4 rounded-sm bg-gray-100" style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
        <iframe
          title={title}
          src={link + '?hideEmbedTopBar=true'}
          allowFullScreen
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
        ></iframe>
      </div>
    </div>
  );
};

const GuideItem = ({
  title,
  description,
  icon,
  onClick,
  status,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
  status: GuideState;
}) => {
  const guideCompleted = status === GuideState.COMPLETED;
  return (
    <div
      onClick={onClick}
      className={`group flex w-full cursor-pointer items-center justify-between rounded-lg border px-3 py-2 text-xs transition-all duration-300 ${
        guideCompleted
          ? 'border-green-200 bg-green-50 hover:bg-green-100'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
      }`}
      data-testid={`guide-${title}-${guideCompleted ? 'complete' : 'incomplete'}`}
    >
      <div className="flex items-center gap-2">
        <span className={`scale-75 ${guideCompleted ? 'text-green-600' : ''}`}>{icon}</span>
        <p className={`font-medium ${guideCompleted ? 'text-green-700' : 'text-gray-900'}`}>{title}</p>
      </div>

      <div className="flex items-center gap-2">
        {guideCompleted ? (
          <FontAwesomeIcon icon={faCircleCheck} className="text-green-500" />
        ) : (
          <FontAwesomeIcon
            icon={faCirclePlay}
            className="text-gray-400 transition-colors duration-200 group-hover:text-gray-600"
          />
        )}
      </div>
    </div>
  );
};

export const Guides = ({ user }: { user: OrgUserWithMetadata }) => {
  const [guides, setGuides] = useState<Guide[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<React.ReactNode | null>(null);
  const [completedGuides, setCompletedGuides] = useState<string[]>(user.completedGuides);
  const authInfo = useAuthInfo();
  const orgs = authInfo.orgHelper?.getOrgs();
  const asignedOrg = orgs?.length && (orgs[0] as OrgMemberInfo);
  const assignedRole = asignedOrg && asignedOrg?.userAssignedRole;
  const isAdmin = assignedRole === 'Admin';

  useEffect(() => {
    if (completedGuides) {
      // Set guide state based on if the guide name is in completed guides array
      const GUIDES_WITH_STATUS: Guide[] = GUIDES_WITHOUT_STATUS.map((guide) => ({
        ...guide,
        status: completedGuides.includes(guide.value) ? GuideState.COMPLETED : GuideState.NOT_STARTED,
      })).sort((a, _) => (a.status === GuideState.COMPLETED ? 1 : -1));

      setGuides(GUIDES_WITH_STATUS);
    }
  }, [completedGuides]);

  const handleOpenModal = (contentValue: string) => {
    setModalContent(
      <GuideModal
        title={MODAL_CONTENT[contentValue].title}
        description={MODAL_CONTENT[contentValue].description}
        guideName={contentValue}
        link={MODAL_CONTENT[contentValue].link}
        state={completedGuides.includes(contentValue) ? GuideState.COMPLETED : GuideState.NOT_STARTED}
        onCompleteGuide={() => setCompletedGuides([...completedGuides, contentValue])}
      />,
    );
    setModalOpen(true);
  };

  return (
    <>
      {guides.length > 0 && (
        <div className="flex w-full flex-col">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">Guides</h2>
          <div className="grid w-full grid-cols-2 gap-2" data-testid="homepage-guides">
            {' '}
            {guides.map((guide) => {
              if (guide.value === 'creating-a-matter' && !isAdmin) return null;
              return (
                <GuideItem
                  key={guide.title}
                  title={guide.title}
                  description={guide.description}
                  icon={guide.icon}
                  onClick={() => {
                    trackCustomEvent(TrackingEvents.ViewHomepageGuide, { type: guide.title });
                    handleOpenModal(guide.value);
                  }}
                  status={guide.status}
                />
              );
            })}
          </div>

          <CustomModal
            isOpen={modalOpen}
            handleClose={() => setModalOpen(false)}
            content={modalContent}
            maxWidth="52rem"
          />
        </div>
      )}
    </>
  );
};
